/**
 * See wiki for annotation key guidelines and Hermes specific annotation keys
 * https://dev.azure.com/xboxstudios/Hermes/_wiki/wikis/Docs/543
 * https://dev.azure.com/xboxstudios/Hermes/_wiki/wikis/Docs/604
 */

export const HermesAnnotationKeys = {
  /** Indicates a time-restricted pause state on a device or operation */
  pause: "hermes.xboxstudios.com/paused-until" as string,
  /** Indicates the property key that holds the build id on a Version  */
  buildId: "hermes.xboxstudios.com/build-id-key" as string,
  /** Indicates the property key that holds structured install options for all platform types.
   * Value is a JSON string containing typed install options with schema versioning.
   */
  installOptions: "hermes.xboxstudios.com/install-options" as string,
  /** Indicates the last time a device received a new refresh token */
  lastVerified: "hermes.xboxstudios.com/last-verified" as string,
  /** Carries PS5/Switch redeploy targets for an install Operation.
   * Value is a JSON string: { ps5: string[], switch: string[] }
   */
  redeployTargets: "hermes.xboxstudios.com/redeploy-targets" as string,
  /** Written back by the client to report per-device redeploy results.
   * Verify format with client code for correct usage.
   * Value is a JSON string: { [deviceName: string]: { success: boolean; error?: string } }
   */
  redeployResults: "hermes.xboxstudios.com/redeploy-results" as string,
};

/**
 * Resource-specific annotation keys used by Hermes for specific functionality.
 * Ideally these should be used for testing out new features before committing
 * them to the official Hermes API.
 */
export const HermesAnnotationConstants = {
  device: {
    pause: {
      key: HermesAnnotationKeys.pause,
      label: "Paused Until",
      description:
        "Indicates a time-restricted pause state on a device or operation. The value should be a timestamp in RFC3339 format.",
    },
  },
  product: {
    installOptions: {
      key: HermesAnnotationKeys.installOptions,
      label: "Default Install Options",
      description:
        "These options serve as defaults when installing any version or channel of this product, unless overridden by channel, version, or user-specified options. Contains a JSON object with versioned schema supporting multiple option types (Xbox, PC, etc.).",
    },
  },
  channel: {
    installOptions: {
      key: HermesAnnotationKeys.installOptions,
      label: "Default Install Options",
      description:
        "These options serve as defaults when installing this channel, unless overridden by version or user-specified options. Contains a JSON object with versioned schema supporting multiple option types (Xbox, PC, etc.).",
    },
    groupingBranch: {
      key: "hermes.xboxstudios.com/grouping.branch",
      label: "Branch",
      description:
        "Groups this channel by branch in the channel list (e.g. 'dev', 'release').",
    },
    groupingPlatform: {
      key: "hermes.xboxstudios.com/grouping.platform",
      label: "Platform",
      description:
        "Groups this channel by platform in the channel list (e.g. 'xbox', 'pc', 'ps5').",
    },
    groupingConfiguration: {
      key: "hermes.xboxstudios.com/grouping.configuration",
      label: "Configuration",
      description:
        "Groups this channel by configuration in the channel list (e.g. 'debug', 'test', 'release').",
    },
  },
  version: {
    installOptions: {
      key: HermesAnnotationKeys.installOptions,
      label: "Default Install Options",
      description:
        "These options serve as defauls when installing this version, unless overridden by user-specified options during installation. Contains a JSON object with versioned schema supporting multiple option types (Xbox, PC, etc.).",
    },
  },
  operation: {
    redeployTargets: {
      key: HermesAnnotationKeys.redeployTargets,
      label: "Redeploy Targets",
      description:
        "Per-platform device names to redeploy to after a successful install. JSON: { ps5: string[], switch: string[] }",
    },
    redeployResults: {
      key: HermesAnnotationKeys.redeployResults,
      label: "Redeploy Results",
      description:
        "Per-device redeploy outcomes written by the client after the post-install redeploy phase. JSON: { [deviceName: string]: boolean }",
    },
  },
};

/**
 * Ordered list of channel annotation fields used for grouping and faceted filtering.
 * Order defines the nesting hierarchy: branch → platform → configuration.
 */
export const CHANNEL_GROUP_FIELDS = [
  {
    key: "hermes.xboxstudios.com/grouping.branch",
    label: "Branch",
    colorScheme: "purple",
  },
  {
    key: "hermes.xboxstudios.com/grouping.platform",
    label: "Platform",
    colorScheme: "teal",
  },
  {
    key: "hermes.xboxstudios.com/grouping.configuration",
    label: "Configuration",
    colorScheme: "orange",
  },
] as const;
